import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import {
  Building2, Plus, Edit, Trash2, Users, ChevronDown, ChevronUp,
  Download, Search, UserPlus, CheckCircle, XCircle, AlertCircle,
  Clock, MapPin, Globe, Mail, Phone, X, FileText, ArrowUp, ArrowDown
} from 'lucide-react';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const API = process.env.REACT_APP_BACKEND_URL;

const ORGANIZATION_TYPES = ["OBNL", "SNCL", "INC", "COOP", "OSBL", "SARL", "SAS", "SA", "EIRL", "Association", "Federation", "Club", "Other"];
const MEMBERSHIP_TYPES = ["Annual", "Monthly", "Weekly", "Seasonal", "Lifetime"];

const emptyOrg = {
  name: '', logo_url: '', address_number: '', address_street: '', city: '',
  province: '', country: '', postal_code: '', organization_type: 'OBNL',
  membership_type: 'Annual', validity_period_start: 'January 1st',
  validity_period_end: 'December 31st', validity_years: '2025-2026',
  website: '', email: '', phone: ''
};

const OrganizationsTab = ({ t, language, freedivers = [], currentUser = null }) => {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [form, setForm] = useState({ ...emptyOrg });
  const [expandedOrgId, setExpandedOrgId] = useState(null);
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [selectedFreediver, setSelectedFreediver] = useState(null);
  const [memberValidityYears, setMemberValidityYears] = useState('2025-2026');
  const [addMemberError, setAddMemberError] = useState('');
  const [memberSortCol, setMemberSortCol] = useState(null);
  const [memberSortDir, setMemberSortDir] = useState('asc');

  const isAdmin = currentUser?.role === 'admin';
  const assignedOrgIds = currentUser?.assigned_organization_ids || [];

  const fetchOrganizations = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/organizations`);
      let allOrgs = res.data.organizations || [];
      // RBAC filter: non-admin users only see assigned organizations
      if (!isAdmin && assignedOrgIds.length > 0) {
        allOrgs = allOrgs.filter(org => assignedOrgIds.includes(org.id));
      } else if (!isAdmin && assignedOrgIds.length === 0) {
        allOrgs = [];
      }
      setOrganizations(allOrgs);
    } catch (e) { console.error('Fetch orgs:', e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchOrganizations(); }, [fetchOrganizations]);

  const fetchMembers = async (orgId) => {
    setMembersLoading(true);
    try {
      const res = await axios.get(`${API}/api/organizations/${orgId}/members`);
      setMembers(res.data.members || []);
    } catch (e) { console.error('Fetch members:', e); setMembers([]); }
    finally { setMembersLoading(false); }
  };

  const toggleExpand = (orgId) => {
    if (expandedOrgId === orgId) {
      setExpandedOrgId(null); setMembers([]);
    } else {
      setExpandedOrgId(orgId); fetchMembers(orgId);
    }
  };

  const handleSaveOrg = async () => {
    try {
      if (editingOrg) {
        await axios.put(`${API}/api/organizations/${editingOrg.id}`, form);
      } else {
        await axios.post(`${API}/api/organizations`, form);
      }
      setShowForm(false); setEditingOrg(null); setForm({ ...emptyOrg });
      fetchOrganizations();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error saving organization');
    }
  };

  const handleEdit = (org) => {
    setEditingOrg(org);
    setForm({ ...emptyOrg, ...org });
    setShowForm(true);
  };

  const handleDelete = async (org) => {
    if (!confirm(language === 'fr' ? `Supprimer "${org.name}"?` : `Delete "${org.name}"?`)) return;
    try {
      await axios.delete(`${API}/api/organizations/${org.id}`);
      fetchOrganizations();
      if (expandedOrgId === org.id) { setExpandedOrgId(null); setMembers([]); }
    } catch (e) {
      alert(e.response?.data?.detail || 'Error deleting organization');
    }
  };

  const handleAddMember = async () => {
    if (!selectedFreediver || !expandedOrgId) return;
    setAddMemberError('');
    try {
      await axios.post(`${API}/api/organizations/${expandedOrgId}/members`, {
        freediver_id: selectedFreediver.id,
        validity_years: memberValidityYears
      });
      setShowAddMember(false); setSelectedFreediver(null); setMemberSearch('');
      fetchMembers(expandedOrgId);
      fetchOrganizations();
    } catch (e) {
      setAddMemberError(e.response?.data?.detail || 'Error adding member');
    }
  };

  const handleRemoveMember = async (membershipId) => {
    if (!confirm(language === 'fr' ? 'Retirer ce membre?' : 'Remove this member?')) return;
    try {
      await axios.delete(`${API}/api/memberships/${membershipId}`);
      fetchMembers(expandedOrgId);
      fetchOrganizations();
    } catch (e) { alert('Error removing member'); }
  };

  const getMedicalStatus = (fd) => {
    if (!fd.medical_certificate_valid) return 'missing';
    if (fd.medical_certificate_expiry_date) {
      const exp = new Date(fd.medical_certificate_expiry_date);
      if (exp < new Date()) return 'expired';
      const daysLeft = Math.ceil((exp - new Date()) / (1000 * 60 * 60 * 24));
      if (daysLeft <= 30) return 'expiring_soon';
    }
    return 'valid';
  };

  const getMedicalBadge = (status) => {
    switch (status) {
      case 'valid': return <Badge className="bg-green-600 text-xs"><CheckCircle size={12} className="mr-1" />{t('valid') || 'Valid'}</Badge>;
      case 'expired': return <Badge className="bg-red-600 text-xs"><XCircle size={12} className="mr-1" />{t('expired') || 'Expired'}</Badge>;
      case 'expiring_soon': return <Badge className="bg-yellow-600 text-xs"><AlertCircle size={12} className="mr-1" />{t('expiringSoon') || 'Expiring Soon'}</Badge>;
      default: return <Badge className="bg-gray-600 text-xs"><XCircle size={12} className="mr-1" />{t('missing') || 'Missing'}</Badge>;
    }
  };

  const getValidityBadge = (m) => {
    const years = m.validity_years || '';
    const endYear = parseInt(years.split('-')[1] || years);
    const now = new Date();
    const currentYear = now.getFullYear();
    if (endYear < currentYear) return <Badge className="bg-red-600/80 text-xs"><Clock size={12} className="mr-1" />{t('expired') || 'Expired'}</Badge>;
    if (endYear === currentYear && now.getMonth() >= 6) return <Badge className="bg-yellow-600 text-xs"><Clock size={12} className="mr-1" />{t('expiringSoon') || 'Expiring'}</Badge>;
    return <Badge className="bg-green-600 text-xs"><CheckCircle size={12} className="mr-1" />{t('active') || 'Active'}</Badge>;
  };

  const exportMembersPDF = (org) => {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    let y = 12;

    // Header
    doc.setFontSize(18); doc.setFont('helvetica', 'bold'); doc.setTextColor(30, 58, 95);
    doc.text(org.name, 148, y, { align: 'center' }); y += 7;

    doc.setFontSize(10); doc.setFont('helvetica', 'normal'); doc.setTextColor(80, 80, 80);
    const addr = [org.address_number, org.address_street, org.city, org.province, org.country, org.postal_code].filter(Boolean).join(', ');
    if (addr) { doc.text(addr, 148, y, { align: 'center' }); y += 5; }
    doc.text(`${org.organization_type} | ${org.membership_type} | ${org.validity_years || ''}`, 148, y, { align: 'center' }); y += 5;
    if (org.validity_period_start && org.validity_period_end) {
      doc.text(`${language === 'fr' ? 'Periode' : 'Period'}: ${org.validity_period_start} - ${org.validity_period_end}`, 148, y, { align: 'center' }); y += 5;
    }

    doc.setDrawColor(30, 58, 95); doc.setLineWidth(0.5); doc.line(10, y, 287, y); y += 5;
    doc.setFontSize(13); doc.setFont('helvetica', 'bold'); doc.setTextColor(30, 58, 95);
    doc.text(language === 'fr' ? 'Liste des membres' : 'Members List', 148, y, { align: 'center' }); y += 5;
    doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(80, 80, 80);
    doc.text(`${members.length} ${language === 'fr' ? 'membres' : 'members'} - ${language === 'fr' ? 'Genere le' : 'Generated'} ${new Date().toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-GB')}`, 148, y, { align: 'center' }); y += 7;

    const tableData = members.map(m => {
      const fd = m.freediver || {};
      const medStatus = getMedicalStatus(fd);
      const years = m.validity_years || '';
      const endYear = parseInt(years.split('-')[1] || years);
      const isActive = endYear >= new Date().getFullYear();
      return [
        (fd.last_name || '').toUpperCase(),
        fd.first_name || '',
        fd.gender === 'male' ? 'M' : fd.gender === 'female' ? 'F' : '-',
        fd.nationality || '-',
        fd.date_of_birth ? new Date(fd.date_of_birth).toLocaleDateString('fr-FR') : '-',
        fd.email || '-',
        fd.phone_number || '-',
        m.validity_years || '-',
        isActive ? (language === 'fr' ? 'Actif' : 'Active') : (language === 'fr' ? 'Expire' : 'Expired'),
        medStatus === 'valid' ? (language === 'fr' ? 'Valide' : 'Valid') :
          medStatus === 'expired' ? (language === 'fr' ? 'Expire' : 'Expired') :
          medStatus === 'expiring_soon' ? (language === 'fr' ? 'Bientot' : 'Soon') :
          (language === 'fr' ? 'Manquant' : 'Missing')
      ];
    });

    doc.setTextColor(0, 0, 0);
    autoTable(doc, {
      startY: y,
      head: [[language === 'fr' ? 'NOM' : 'NAME', language === 'fr' ? 'PRENOM' : 'FIRST', 'S', 'NAT', language === 'fr' ? 'DATE NAISS.' : 'DOB', 'EMAIL', language === 'fr' ? 'TEL' : 'PHONE', language === 'fr' ? 'ANNEE(S)' : 'YEAR(S)', language === 'fr' ? 'STATUT' : 'STATUS', language === 'fr' ? 'CERT. MED.' : 'MED. CERT.']],
      body: tableData, theme: 'grid',
      styles: { fontSize: 7, cellPadding: 1.5, halign: 'center' },
      headStyles: { fillColor: [30, 58, 95], textColor: 255, fontStyle: 'bold', fontSize: 7 },
      alternateRowStyles: { fillColor: [240, 245, 255] },
      margin: { left: 10, right: 10 },
      didParseCell: (data) => {
        if (data.section === 'body') {
          if (data.column.index === 8) {
            data.cell.styles.textColor = data.cell.raw === 'Active' || data.cell.raw === 'Actif' ? [20, 120, 20] : [200, 40, 40];
            data.cell.styles.fontStyle = 'bold';
          }
          if (data.column.index === 9) {
            const v = data.cell.raw;
            if (v === 'Valid' || v === 'Valide') data.cell.styles.textColor = [20, 120, 20];
            else if (v === 'Expired' || v === 'Expire') data.cell.styles.textColor = [200, 40, 40];
            else if (v === 'Soon' || v === 'Bientot') data.cell.styles.textColor = [180, 140, 0];
            else data.cell.styles.textColor = [128, 128, 128];
          }
        }
      }
    });

    const pages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pages; i++) {
      doc.setPage(i); doc.setFontSize(8); doc.setTextColor(128);
      doc.text(`Page ${i}/${pages} | ${org.name} | Apnea Coach Manager`, 148, doc.internal.pageSize.height - 8, { align: 'center' });
    }
    doc.save(`Members_${org.name.replace(/\s+/g, '_')}.pdf`);
  };

  const filteredFreedivers = freedivers.filter(fd => {
    if (!memberSearch) return true;
    const q = memberSearch.toLowerCase();
    return (fd.first_name || '').toLowerCase().includes(q) || (fd.last_name || '').toLowerCase().includes(q) || (fd.email || '').toLowerCase().includes(q);
  });

  const currentYear = new Date().getFullYear();

  const handleMemberSort = (col) => {
    if (memberSortCol === col) {
      setMemberSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setMemberSortCol(col);
      setMemberSortDir('asc');
    }
  };

  const sortedMembers = [...members].sort((a, b) => {
    if (!memberSortCol) return 0;
    const fdA = freedivers.find(f => f.id === a.freediver_id) || {};
    const fdB = freedivers.find(f => f.id === b.freediver_id) || {};
    let valA, valB;
    switch (memberSortCol) {
      case 'last_name': valA = (fdA.last_name || '').toLowerCase(); valB = (fdB.last_name || '').toLowerCase(); break;
      case 'first_name': valA = (fdA.first_name || '').toLowerCase(); valB = (fdB.first_name || '').toLowerCase(); break;
      case 'gender': valA = fdA.gender || ''; valB = fdB.gender || ''; break;
      case 'nationality': valA = fdA.nationality || ''; valB = fdB.nationality || ''; break;
      default: return 0;
    }
    if (valA < valB) return memberSortDir === 'asc' ? -1 : 1;
    if (valA > valB) return memberSortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const SortableHeader = ({ col, label }) => (
    <TableHead
      className="text-blue-300 text-xs cursor-pointer hover:text-white select-none"
      onClick={() => handleMemberSort(col)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {memberSortCol === col && (memberSortDir === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />)}
      </span>
    </TableHead>
  );
  const validityYearOptions = [
    `${currentYear - 1}-${currentYear}`,
    `${currentYear}-${currentYear + 1}`,
    `${currentYear + 1}-${currentYear + 2}`
  ];

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <h2 className="text-2xl md:text-3xl font-bold text-blue-200" data-testid="organizations-title">
          <Building2 className="inline mr-2" size={28} />
          {t('organizations') || 'Organization(s)'}
        </h2>
        <Button onClick={() => { setShowForm(true); setEditingOrg(null); setForm({ ...emptyOrg }); }}
          className="bg-gradient-to-r from-teal-600 to-blue-600 hover:from-teal-700 hover:to-blue-700 text-white" data-testid="add-organization-btn">
          <Plus size={16} className="mr-1.5" />{t('addOrganization') || 'Add Organization'}
        </Button>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <Card className="bg-gradient-to-br from-teal-900/40 to-blue-900/40 border-teal-700/30" data-testid="org-form">
          <CardHeader>
            <CardTitle className="text-teal-200 flex items-center justify-between">
              <span>{editingOrg ? (t('editOrganization') || 'Edit Organization') : (t('addOrganization') || 'Add Organization')}</span>
              <Button onClick={() => { setShowForm(false); setEditingOrg(null); }} variant="ghost" size="sm" className="text-blue-300 hover:text-white"><X size={18} /></Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div><Label className="text-blue-200 text-xs">{t('organizationName') || 'Organization Name'} *</Label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="bg-blue-800/40 border-blue-600 text-white" data-testid="org-name-input" /></div>
              <div><Label className="text-blue-200 text-xs">{t('organizationType') || 'Type'}</Label>
                <Select value={form.organization_type} onValueChange={v => setForm({ ...form, organization_type: v })}>
                  <SelectTrigger className="bg-blue-800/40 border-blue-600 text-white" data-testid="org-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{ORGANIZATION_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="text-blue-200 text-xs">{t('membershipType') || 'Membership Type'}</Label>
                <Select value={form.membership_type} onValueChange={v => setForm({ ...form, membership_type: v })}>
                  <SelectTrigger className="bg-blue-800/40 border-blue-600 text-white" data-testid="org-membership-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{MEMBERSHIP_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="text-blue-200 text-xs">{t('addressNumber') || 'Number'}</Label>
                <Input value={form.address_number} onChange={e => setForm({ ...form, address_number: e.target.value })} className="bg-blue-800/40 border-blue-600 text-white" /></div>
              <div><Label className="text-blue-200 text-xs">{t('street') || 'Street'}</Label>
                <Input value={form.address_street} onChange={e => setForm({ ...form, address_street: e.target.value })} className="bg-blue-800/40 border-blue-600 text-white" /></div>
              <div><Label className="text-blue-200 text-xs">{t('city') || 'City'}</Label>
                <Input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} className="bg-blue-800/40 border-blue-600 text-white" /></div>
              <div><Label className="text-blue-200 text-xs">{t('province') || 'Province'}</Label>
                <Input value={form.province} onChange={e => setForm({ ...form, province: e.target.value })} className="bg-blue-800/40 border-blue-600 text-white" /></div>
              <div><Label className="text-blue-200 text-xs">{t('country') || 'Country'}</Label>
                <Input value={form.country} onChange={e => setForm({ ...form, country: e.target.value })} className="bg-blue-800/40 border-blue-600 text-white" /></div>
              <div><Label className="text-blue-200 text-xs">{t('postalCode') || 'Postal Code'}</Label>
                <Input value={form.postal_code} onChange={e => setForm({ ...form, postal_code: e.target.value })} className="bg-blue-800/40 border-blue-600 text-white" /></div>
              <div><Label className="text-blue-200 text-xs">{t('validityPeriodStart') || 'Validity Start'}</Label>
                <Input value={form.validity_period_start} onChange={e => setForm({ ...form, validity_period_start: e.target.value })} placeholder="January 1st" className="bg-blue-800/40 border-blue-600 text-white" /></div>
              <div><Label className="text-blue-200 text-xs">{t('validityPeriodEnd') || 'Validity End'}</Label>
                <Input value={form.validity_period_end} onChange={e => setForm({ ...form, validity_period_end: e.target.value })} placeholder="December 31st" className="bg-blue-800/40 border-blue-600 text-white" /></div>
              <div><Label className="text-blue-200 text-xs">{t('validityYears') || 'Year(s) of Validity'}</Label>
                <Input value={form.validity_years} onChange={e => setForm({ ...form, validity_years: e.target.value })} placeholder="2025-2026" className="bg-blue-800/40 border-blue-600 text-white" /></div>
              <div><Label className="text-blue-200 text-xs">{t('website') || 'Website'}</Label>
                <Input value={form.website} onChange={e => setForm({ ...form, website: e.target.value })} className="bg-blue-800/40 border-blue-600 text-white" /></div>
              <div><Label className="text-blue-200 text-xs">Email</Label>
                <Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="bg-blue-800/40 border-blue-600 text-white" /></div>
              <div><Label className="text-blue-200 text-xs">{t('phone') || 'Phone'}</Label>
                <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="bg-blue-800/40 border-blue-600 text-white" /></div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button onClick={() => { setShowForm(false); setEditingOrg(null); }} variant="outline" className="border-blue-600 text-blue-300">{t('cancel') || 'Cancel'}</Button>
              <Button onClick={handleSaveOrg} className="bg-teal-600 hover:bg-teal-700 text-white" disabled={!form.name} data-testid="save-org-btn">
                {editingOrg ? (t('update') || 'Update') : (t('create') || 'Create')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Organizations List */}
      <Card className="bg-gradient-to-br from-blue-800/40 to-purple-800/40 border-blue-700/30">
        <CardHeader className="pb-2">
          <CardTitle className="text-blue-200 flex items-center">
            <Building2 className="mr-2" size={20} />
            {t('organizationsList') || 'Organizations'}
            {organizations.length > 0 && <Badge className="ml-2 bg-blue-700/50">{organizations.length}</Badge>}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-blue-400">{t('loading') || 'Loading...'}</div>
          ) : organizations.length === 0 ? (
            <div className="text-center py-8">
              <Building2 size={48} className="mx-auto text-blue-400 mb-4 opacity-40" />
              <p className="text-blue-300">{t('noOrganizations') || 'No organizations yet'}</p>
              <p className="text-blue-400 text-sm mt-1">{t('addOrganizationHint') || 'Click "Add Organization" to get started'}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {organizations.map(org => {
                const isExpanded = expandedOrgId === org.id;
                return (
                  <div key={org.id} className="rounded-lg overflow-hidden" data-testid={`org-row-${org.id}`}>
                    {/* Organization Row */}
                    <div className={`p-3 md:p-4 border cursor-pointer transition-colors ${isExpanded ? 'bg-teal-900/30 border-teal-600 rounded-t-lg' : 'bg-blue-800/30 border-blue-700/30 hover:bg-blue-700/30 rounded-lg'}`}
                      onClick={() => toggleExpand(org.id)}>
                      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-2">
                        <div className="flex items-center gap-3 flex-1">
                          <div className="w-10 h-10 rounded-lg bg-teal-800/60 border border-teal-700/40 flex items-center justify-center shrink-0">
                            {org.logo_url ? <img src={org.logo_url} alt="" className="w-8 h-8 object-contain rounded" /> : <Building2 size={20} className="text-teal-400" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-white font-semibold text-sm md:text-base truncate">{org.name}</h4>
                            <div className="flex items-center gap-2 flex-wrap text-xs text-blue-400">
                              {org.city && <span className="flex items-center gap-1"><MapPin size={10} />{org.city}{org.province ? `, ${org.province}` : ''}</span>}
                              {org.email && <span className="flex items-center gap-1"><Mail size={10} />{org.email}</span>}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge className="bg-indigo-700/60 text-xs">{org.organization_type}</Badge>
                            <Badge className="bg-teal-700/60 text-xs">{org.membership_type}</Badge>
                            {org.validity_years && <Badge className="bg-blue-700/60 text-xs">{org.validity_years}</Badge>}
                            <Badge className="bg-purple-700/50 text-xs"><Users size={12} className="mr-1" />{org.member_count || 0}</Badge>
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
                          <Button onClick={() => handleEdit(org)} size="sm" variant="outline" className="border-teal-600 text-teal-300 hover:bg-teal-700/30" data-testid={`edit-org-${org.id}`}>
                            <Edit size={14} />
                          </Button>
                          <Button onClick={() => handleDelete(org)} size="sm" variant="outline" className="border-red-600 text-red-300 hover:bg-red-700/30" data-testid={`delete-org-${org.id}`}>
                            <Trash2 size={14} />
                          </Button>
                          {isExpanded ? <ChevronUp size={18} className="text-teal-400 ml-1" /> : <ChevronDown size={18} className="text-blue-400 ml-1" />}
                        </div>
                      </div>
                    </div>

                    {/* Expanded Members Panel */}
                    {isExpanded && (
                      <div className="bg-blue-950/60 border border-teal-700/30 border-t-0 rounded-b-lg" data-testid={`org-members-${org.id}`}>
                        {/* Org details bar */}
                        <div className="px-4 py-3 bg-teal-900/20 border-b border-teal-700/20 flex flex-wrap items-center justify-between gap-2">
                          <div className="flex items-center gap-4 text-sm text-blue-300">
                            {org.validity_period_start && org.validity_period_end && (
                              <span>{t('period') || 'Period'}: {org.validity_period_start} - {org.validity_period_end}</span>
                            )}
                            {org.website && <a href={org.website.startsWith('http') ? org.website : `https://${org.website}`} target="_blank" rel="noopener noreferrer" className="text-teal-400 hover:text-teal-300 flex items-center gap-1"><Globe size={13} />{org.website}</a>}
                            {org.phone && <span className="flex items-center gap-1"><Phone size={13} />{org.phone}</span>}
                          </div>
                          <div className="flex items-center gap-2">
                            <Button onClick={() => { setShowAddMember(true); setAddMemberError(''); setSelectedFreediver(null); setMemberSearch(''); }}
                              size="sm" className="bg-teal-600 hover:bg-teal-700 text-white" data-testid={`add-member-${org.id}`}>
                              <UserPlus size={14} className="mr-1" />{t('addMember') || 'Add Member'}
                            </Button>
                            <Button onClick={() => exportMembersPDF(org)} size="sm" className="bg-gradient-to-r from-red-600 to-orange-600 text-white" disabled={members.length === 0} data-testid={`export-members-pdf-${org.id}`}>
                              <Download size={14} className="mr-1" />PDF
                            </Button>
                          </div>
                        </div>

                        {/* Add Member Popup */}
                        {showAddMember && (
                          <div className="p-4 bg-teal-900/30 border-b border-teal-700/20" data-testid="add-member-panel">
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="text-teal-200 font-semibold">{t('addMemberTo') || 'Add Member to'} {org.name}</h4>
                              <Button onClick={() => setShowAddMember(false)} variant="ghost" size="sm" className="text-blue-300"><X size={16} /></Button>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                              <div className="md:col-span-2">
                                <Label className="text-blue-200 text-xs mb-1 block">{t('searchFreediver') || 'Search Freediver'}</Label>
                                <div className="relative">
                                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-400" />
                                  <Input value={memberSearch} onChange={e => { setMemberSearch(e.target.value); setSelectedFreediver(null); }}
                                    placeholder={language === 'fr' ? 'Rechercher par nom, prenom ou email...' : 'Search by name or email...'}
                                    className="bg-blue-800/40 border-blue-600 text-white pl-9" data-testid="member-search-input" />
                                </div>
                                {memberSearch && !selectedFreediver && (
                                  <div className="mt-1 max-h-40 overflow-y-auto bg-blue-900/80 border border-blue-700/50 rounded-lg">
                                    {filteredFreedivers.slice(0, 10).map(fd => (
                                      <div key={fd.id} onClick={() => { setSelectedFreediver(fd); setMemberSearch(`${fd.first_name} ${fd.last_name}`); }}
                                        className="px-3 py-2 hover:bg-blue-700/50 cursor-pointer text-sm text-blue-100 flex justify-between" data-testid={`freediver-option-${fd.id}`}>
                                        <span className="font-semibold">{fd.first_name} {fd.last_name}</span>
                                        <span className="text-blue-400">{fd.email}</span>
                                      </div>
                                    ))}
                                    {filteredFreedivers.length === 0 && <div className="px-3 py-2 text-blue-400 text-sm">{t('noResults') || 'No results'}</div>}
                                  </div>
                                )}
                                {selectedFreediver && (
                                  <div className="mt-1 text-sm text-teal-300">
                                    <CheckCircle size={14} className="inline mr-1" />{t('selected') || 'Selected'}: <span className="font-semibold text-white">{selectedFreediver.first_name} {selectedFreediver.last_name}</span>
                                  </div>
                                )}
                              </div>
                              <div>
                                <Label className="text-blue-200 text-xs mb-1 block">{t('validityYears') || 'Year(s) of Validity'}</Label>
                                <Select value={memberValidityYears} onValueChange={setMemberValidityYears}>
                                  <SelectTrigger className="bg-blue-800/40 border-blue-600 text-white" data-testid="member-validity-select"><SelectValue /></SelectTrigger>
                                  <SelectContent>{validityYearOptions.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
                                </Select>
                              </div>
                            </div>
                            {addMemberError && <p className="text-red-400 text-sm mt-2" data-testid="add-member-error">{addMemberError}</p>}
                            <div className="flex justify-end mt-3">
                              <Button onClick={handleAddMember} className="bg-teal-600 hover:bg-teal-700 text-white" disabled={!selectedFreediver} data-testid="confirm-add-member">
                                <UserPlus size={14} className="mr-1" />{t('addMember') || 'Add Member'}
                              </Button>
                            </div>
                          </div>
                        )}

                        {/* Members Table */}
                        <div className="max-h-[50vh] overflow-y-auto">
                          {membersLoading ? (
                            <div className="text-center py-6 text-blue-400">{t('loading') || 'Loading...'}</div>
                          ) : members.length === 0 ? (
                            <div className="text-center py-8 text-blue-400">
                              <Users size={36} className="mx-auto mb-2 opacity-40" />
                              <p>{t('noMembers') || 'No members registered yet'}</p>
                            </div>
                          ) : (
                            <div className="overflow-x-auto">
                              <Table>
                                <TableHeader>
                                  <TableRow className="border-blue-700/30">
                                    <SortableHeader col="last_name" label={t('lastName') || 'Last Name'} />
                                    <SortableHeader col="first_name" label={t('firstName') || 'First Name'} />
                                    <SortableHeader col="gender" label={t('sex') || 'Sex'} />
                                    <SortableHeader col="nationality" label={t('nationality') || 'Nat.'} />
                                    <TableHead className="text-blue-300 text-xs hidden lg:table-cell">Email</TableHead>
                                    <TableHead className="text-blue-300 text-xs hidden lg:table-cell">{t('phone') || 'Phone'}</TableHead>
                                    <TableHead className="text-blue-300 text-xs">{t('validityYears') || 'Year(s)'}</TableHead>
                                    <TableHead className="text-blue-300 text-xs">{t('validity') || 'Validity'}</TableHead>
                                    <TableHead className="text-blue-300 text-xs">{t('medicalCertificate') || 'Med. Cert.'}</TableHead>
                                    <TableHead className="text-blue-300 text-xs w-10"></TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {sortedMembers.map(m => {
                                    const fd = m.freediver || freedivers.find(f => f.id === m.freediver_id) || {};
                                    return (
                                      <TableRow key={m.id} className="border-blue-700/20">
                                        <TableCell className="text-white font-semibold text-sm">{(fd.last_name || '').toUpperCase()}</TableCell>
                                        <TableCell className="text-blue-100 text-sm">{fd.first_name || '-'}</TableCell>
                                        <TableCell className="text-blue-200 text-xs hidden md:table-cell">{fd.gender === 'male' ? 'M' : fd.gender === 'female' ? 'F' : '-'}</TableCell>
                                        <TableCell className="text-blue-200 text-xs hidden md:table-cell">{fd.nationality || '-'}</TableCell>
                                        <TableCell className="text-blue-300 text-xs hidden lg:table-cell">{fd.email || '-'}</TableCell>
                                        <TableCell className="text-blue-300 text-xs hidden lg:table-cell">{fd.phone_number || '-'}</TableCell>
                                        <TableCell className="text-blue-200 text-xs font-mono">{m.validity_years}</TableCell>
                                        <TableCell>{getValidityBadge(m)}</TableCell>
                                        <TableCell>{getMedicalBadge(getMedicalStatus(fd))}</TableCell>
                                        <TableCell>
                                          <Button onClick={() => handleRemoveMember(m.id)} size="sm" variant="ghost" className="text-red-400 hover:text-red-300 hover:bg-red-900/30 p-1" data-testid={`remove-member-${m.id}`}>
                                            <Trash2 size={14} />
                                          </Button>
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default OrganizationsTab;
